import React, { useState, useEffect } from 'react';
import { X, Calendar, CheckCircle2, XCircle, ExternalLink, FileText, Edit2, Clock } from 'lucide-react';
import { format } from 'date-fns';
import TaskModal from './TaskModal';
import { projectBoardService } from '../../api/boardApi';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../../constants/brandPalette';

// ── Helpers ───────────────────────────────────────────────────────────────────

const AVATAR_PALETTE = ['#37559e','#0f766e','#7c3aed','#b45309','#be185d','#0369a1','#15803d','#c2410c'];
const getInitials    = (name = '') => name.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?';
const avatarColor    = (name = '') => AVATAR_PALETTE[(name?.charCodeAt(0) ?? 0) % AVATAR_PALETTE.length];

const fmtDate = (d) => {
  if (!d) return null;
  try { return format(new Date(d), 'MMM d, yyyy'); } catch { return null; }
};

const REVIEW_STATUS = {
  approved:          { label: 'Approved',          bg: '#f0fdf4', text: '#15803d', dot: '#22c55e' },
  changes_requested: { label: 'Changes Requested', bg: '#fff1f2', text: '#be123c', dot: '#f43f5e' },
  under_review:      { label: 'Under Review',      bg: '#fffbeb', text: '#92400e', dot: '#f59e0b' },
};

const StatusBadge = ({ statusKey }) => {
  const s = REVIEW_STATUS[statusKey] || REVIEW_STATUS.under_review;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
      style={{ backgroundColor: s.bg, color: s.text }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: s.dot }} />
      {s.label}
    </span>
  );
};

// ── TaskReviewModal ───────────────────────────────────────────────────────────

const TaskReviewModal = ({ isOpen, onClose, task, currentUser, onApprove, onReject, onUpdateTask, projectId, teamMembers = [] }) => {
  const [reviewComment, setReviewComment] = useState('');
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [localReviews, setLocalReviews] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(null); // 'approve' | 'reject' | null

  useEffect(() => { setLocalReviews(task?.review?.comments || []); }, [task]);

  useEffect(() => {
    if (!projectId || !isOpen) return;
    setDocsLoading(true);
    projectBoardService.getProjectDocuments(projectId)
      .then(setDocuments)
      .catch(() => {})
      .finally(() => setDocsLoading(false));
  }, [projectId, isOpen]);

  if (!isOpen || !task) return null;

  const lastReview = localReviews[localReviews.length - 1];
  const overallStatusKey = localReviews.length === 0 ? 'under_review'
    : lastReview?.status === 'approved' ? 'approved'
    : 'changes_requested';

  const buildReview = (status) => ({
    comment:   reviewComment,
    reviewer:  { id: currentUser?.uid || currentUser?.id, name: currentUser?.displayName || currentUser?.name || currentUser?.email },
    status,
    timestamp: new Date().toISOString(),
  });

  const handleApprove = async () => {
    setSubmitting('approve');
    const review = buildReview('approved');
    setLocalReviews(p => [...p, review]);
    setReviewComment('');
    try { await onApprove({ comment: review.comment, reviewer: review.reviewer, timestamp: review.timestamp }); }
    finally { setSubmitting(null); }
  };

  const handleReject = async () => {
    setSubmitting('reject');
    const review = buildReview('changes_requested');
    setLocalReviews(p => [...p, review]);
    setReviewComment('');
    try { await onReject({ comment: review.comment, reviewer: review.reviewer, timestamp: review.timestamp }); }
    finally { setSubmitting(null); }
  };

  const assignees = Object.values(task.assignee || {}).filter(a => a?.name);

  const openDoc = (doc) => {
    if (!doc) return;
    const url = doc.url || doc.fileUrl;
    if (url) window.open(url, '_blank');
  };

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}
      >
        <div
          className="relative w-full sm:max-w-4xl flex flex-col rounded-t-2xl sm:rounded-2xl overflow-hidden border"
          style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft, boxShadow: SYSTEM_COLORS.shadowStrong, maxHeight: '92dvh' }}
        >
          {/* ── Header ─────────────────────────────────────────────────────── */}
          <div
            className="flex items-start justify-between px-5 py-4 flex-shrink-0"
            style={{ backgroundColor: BRAND_COLORS.primary }}
          >
            <div className="space-y-1.5">
              <h2 className="text-base font-bold text-white">Task Review</h2>
              <div className="flex items-center gap-2">
                <StatusBadge statusKey={overallStatusKey} />
                {localReviews.length > 0 && (
                  <span className="text-xs" style={{ color: 'rgba(255,255,255,0.72)' }}>
                    {localReviews.length} {localReviews.length === 1 ? 'review' : 'reviews'}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex items-center justify-center rounded-lg transition-colors flex-shrink-0"
              style={{ width: 32, height: 32, color: 'rgba(255,255,255,0.85)' }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <X size={17} />
            </button>
          </div>

          {/* ── Body ───────────────────────────────────────────────────────── */}
          <div className="flex-1 flex flex-col sm:flex-row overflow-hidden min-h-0">

            {/* Left — Task details */}
            <div
              className="flex-1 overflow-y-auto px-5 py-4 space-y-4"
              style={{ borderRight: `1px solid ${SYSTEM_COLORS.borderSoft}` }}
            >
              {/* Title + edit */}
              <div className="flex items-start gap-2">
                <h3 className="flex-1 text-sm font-bold leading-snug" style={{ color: NEUTRAL_COLORS.black }}>
                  {task.title}
                </h3>
                <button
                  onClick={() => setIsTaskModalOpen(true)}
                  className="flex-shrink-0 flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors"
                  style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}
                >
                  <Edit2 size={11} /> Edit
                </button>
              </div>

              {/* Description */}
              {task.description && (
                <p className="text-sm leading-relaxed" style={{ color: SYSTEM_COLORS.textMuted }}>
                  {task.description}
                </p>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: 'Start', value: fmtDate(task.startDate) },
                  { label: 'Due',   value: fmtDate(task.dueDate) },
                ].map(({ label, value }) => value && (
                  <div
                    key={label}
                    className="rounded-xl px-3 py-2.5"
                    style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle || '#f8fafc' }}
                  >
                    <p className="text-[10px] font-semibold uppercase tracking-widest mb-0.5" style={{ color: SYSTEM_COLORS.textMuted }}>{label}</p>
                    <div className="flex items-center gap-1.5">
                      <Calendar size={12} style={{ color: BRAND_COLORS.primary }} />
                      <span className="text-xs font-medium" style={{ color: NEUTRAL_COLORS.black }}>{value}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Assignees */}
              {assignees.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest mb-2" style={{ color: SYSTEM_COLORS.textMuted }}>Assignees</p>
                  <div className="flex flex-wrap gap-2">
                    {assignees.map((a, i) => (
                      <div
                        key={i}
                        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
                        style={{ backgroundColor: SYSTEM_COLORS.primaryTint, color: BRAND_COLORS.primary }}
                      >
                        <span
                          className="flex items-center justify-center rounded-full text-white font-bold"
                          style={{ width: 18, height: 18, fontSize: 9, backgroundColor: avatarColor(a.name) }}
                        >
                          {getInitials(a.name)}
                        </span>
                        {a.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Evidence */}
              {task.evidence?.length > 0 && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-widest mb-2" style={{ color: SYSTEM_COLORS.textMuted }}>Evidence of Work</p>
                  <div className="space-y-1.5">
                    {docsLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-3.5 w-3.5 border-2" style={{ borderTopColor: BRAND_COLORS.primary, borderColor: SYSTEM_COLORS.borderSoft }} />
                        <span className="text-xs" style={{ color: SYSTEM_COLORS.textMuted }}>Loading…</span>
                      </div>
                    ) : (
                      task.evidence.map((evidenceId, i) => {
                        const doc = documents.find(d => d.id === evidenceId);
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => openDoc(doc)}
                            className="flex items-center gap-2 w-full rounded-xl px-3 py-2 text-left transition-colors"
                            style={{ backgroundColor: SYSTEM_COLORS.surfaceSubtle || '#f8fafc' }}
                            onMouseEnter={e => { e.currentTarget.style.backgroundColor = SYSTEM_COLORS.primaryTint; }}
                            onMouseLeave={e => { e.currentTarget.style.backgroundColor = SYSTEM_COLORS.surfaceSubtle || '#f8fafc'; }}
                          >
                            {doc?.type === 'link'
                              ? <ExternalLink size={13} style={{ color: BRAND_COLORS.primary, flexShrink: 0 }} />
                              : <FileText size={13} style={{ color: BRAND_COLORS.primary, flexShrink: 0 }} />
                            }
                            <span className="text-xs font-medium truncate" style={{ color: NEUTRAL_COLORS.black }}>
                              {doc ? doc.name : 'Loading…'}
                            </span>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right — Review panel */}
            <div className="w-full sm:w-80 flex flex-col overflow-hidden flex-shrink-0">

              {/* Review history */}
              {localReviews.length > 0 && (
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: SYSTEM_COLORS.textMuted }}>Review History</p>
                  {localReviews.map((rev, i) => {
                    const s = REVIEW_STATUS[rev.status] || REVIEW_STATUS.under_review;
                    return (
                      <div
                        key={i}
                        className="rounded-xl p-3 space-y-1.5"
                        style={{ backgroundColor: s.bg, border: `1px solid ${s.dot}22` }}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5">
                            <span
                              className="flex items-center justify-center rounded-full text-white font-bold flex-shrink-0"
                              style={{ width: 20, height: 20, fontSize: 9, backgroundColor: avatarColor(rev.reviewer?.name || '') }}
                            >
                              {getInitials(rev.reviewer?.name || '')}
                            </span>
                            <span className="text-xs font-semibold" style={{ color: NEUTRAL_COLORS.black }}>
                              {rev.reviewer?.name || 'Reviewer'}
                            </span>
                          </div>
                          <span
                            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold flex-shrink-0"
                            style={{ backgroundColor: s.dot + '22', color: s.text }}
                          >
                            <span className="h-1 w-1 rounded-full" style={{ backgroundColor: s.dot }} />
                            {s.label}
                          </span>
                        </div>
                        {rev.comment && (
                          <p className="text-xs leading-relaxed" style={{ color: s.text }}>{rev.comment}</p>
                        )}
                        <div className="flex items-center gap-1" style={{ color: SYSTEM_COLORS.textMuted }}>
                          <Clock size={10} />
                          <span className="text-[10px]">{fmtDate(rev.timestamp) || '—'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Add review */}
              <div
                className="px-4 py-4 space-y-3 flex-shrink-0"
                style={{ borderTop: localReviews.length > 0 ? `1px solid ${SYSTEM_COLORS.borderSoft}` : 'none', paddingTop: localReviews.length === 0 ? 16 : undefined }}
              >
                {localReviews.length === 0 && (
                  <p className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: SYSTEM_COLORS.textMuted }}>Add Review</p>
                )}

                <textarea
                  value={reviewComment}
                  onChange={e => setReviewComment(e.target.value)}
                  placeholder="Write your review comment…"
                  rows={4}
                  className="w-full rounded-xl border px-3 py-2.5 text-sm outline-none transition-colors resize-none"
                  style={{
                    borderColor: SYSTEM_COLORS.borderSoft,
                    backgroundColor: SYSTEM_COLORS.shellSurface,
                    color: NEUTRAL_COLORS.black,
                  }}
                />

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleReject}
                    disabled={!reviewComment.trim() || submitting !== null}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-semibold transition-opacity disabled:opacity-40"
                    style={{ backgroundColor: '#fff1f2', color: '#be123c', border: '1px solid #fecdd3' }}
                  >
                    {submitting === 'reject'
                      ? <div className="animate-spin rounded-full h-3 w-3 border-2 border-[#be123c]/30 border-t-[#be123c]" />
                      : <XCircle size={13} />
                    }
                    Request Changes
                  </button>
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={!reviewComment.trim() || submitting !== null}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl py-2 text-xs font-semibold transition-opacity disabled:opacity-40"
                    style={{ backgroundColor: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}
                  >
                    {submitting === 'approve'
                      ? <div className="animate-spin rounded-full h-3 w-3 border-2 border-[#15803d]/30 border-t-[#15803d]" />
                      : <CheckCircle2 size={13} />
                    }
                    Approve
                  </button>
                </div>

                <button
                  type="button"
                  onClick={onClose}
                  className="w-full rounded-xl py-2 text-xs font-medium transition-colors"
                  style={{ backgroundColor: SYSTEM_COLORS.borderSoft, color: NEUTRAL_COLORS.black }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isTaskModalOpen && (
        <TaskModal
          isOpen={isTaskModalOpen}
          onClose={() => setIsTaskModalOpen(false)}
          task={task}
          onSave={(updated) => { onUpdateTask(updated); setIsTaskModalOpen(false); }}
          projectId={projectId}
          teamMembers={teamMembers}
          hideReviewSection
        />
      )}
    </>
  );
};

export default TaskReviewModal;
